import { buildProduct } from '@hive/platform-url-schema'
import React, { SyntheticEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'

import { isAgeRestrictedProduct } from '@entities/Product/helpers'

import { CartProduct } from '@features/cart/model/useCartStore'

import { PRODUCT_NO_PHOTO_PATH } from '@shared/contants/product'
import { Product } from '@shared/generated'
import { useAgeVerification } from '@shared/hooks'
import { cn } from '@shared/lib/cn'
import ButtonDelay from '@shared/ui/button-delay'

import ActionButtons from './action-buttons'
import PriceBlock from './price-block'
import ControlCart from '../control'
import DeleteProduct from '../delete-product'

import styles from './styles.module.scss'

import type { MouseEventHandler } from 'react'

export interface CartPositionProps {
  key?: string
  item?: CartProduct
  product: Product
  onOutOfStockPositionClick: (e: SyntheticEvent, item: CartProduct | undefined, product: Product) => void
  onLinkClick?: (e: SyntheticEvent, item: CartProduct | undefined, product: Product) => void
  onRemoveRequested: (e: SyntheticEvent | null, id: string) => void
}

const CartPosition: React.FC<CartPositionProps> = ({
  key,
  item,
  product,
  onOutOfStockPositionClick,
  onLinkClick,
  onRemoveRequested,
}) => {
  const OUT_STOCK = !item?.amount
  const navigate = useNavigate()

  const { t: tca } = useTranslation('cart')
  const { isAdult, guardAgeRestrictedAction } = useAgeVerification()

  const [ deleting, setDeleting ] = useState(false)
  const productUrl = buildProduct(product.slug as string)
  const isAgeControlRequired = !isAdult && isAgeRestrictedProduct(product)

  const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (OUT_STOCK) {
      onOutOfStockPositionClick(e, item, product)
    }
    else {
      onLinkClick?.(e, item, product)

      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return
      }

      e.preventDefault()

      guardAgeRestrictedAction(isAgeControlRequired, () => {
        navigate(productUrl)
      })
    }
  }
  const handleDeleteRequested = useCallback((e: SyntheticEvent) => {
    e.preventDefault()
    setDeleting(true)
  }, [ setDeleting ])

  return <div
    className={cn(styles['preview-cart-item'], {
      [styles.out]: OUT_STOCK,
    })}
    key={key}
  >
    {/* Top Row: Image + Product Info */}
    <div className={styles['preview-cart-item__top']}>
      <Link
        className={styles['preview-cart-item__image']}
        onClick={handleLinkClick}
        to={productUrl}
      >
        <img
          src={product.preview?.url || PRODUCT_NO_PHOTO_PATH}
          onError={(e) => {
            const target = e.currentTarget
            if (
              target.src !==
                      window.location.origin + PRODUCT_NO_PHOTO_PATH
            ) {
              target.src = PRODUCT_NO_PHOTO_PATH
            }
          }}
          alt=""
        />
      </Link>

      <div className={styles['preview-cart-item__content']}>
        <div className={styles['preview-cart-item__price-section']}>
          {!OUT_STOCK && (
            <PriceBlock product={product} />
          )}
        </div>

        <Link
          className={styles['preview-cart-item__title']}
          to={productUrl}
          onClick={handleLinkClick}
        >
          {product.name}
        </Link>

        
      </div>
    </div>

    {/* Bottom Row: Control Buttons */}
    {/* Action Buttons */}
    <div className={styles['preview-cart-item__actions']}>
      {deleting
        ? (
          <ButtonDelay
            duration={3000}
            onFinish={() => onRemoveRequested(null, product.id)}
            onClick={() => {
              setDeleting(false)
            }}
            size='small'
          >
            {tca('preview.cart_return')}
          </ButtonDelay>
        )
        : (
          !OUT_STOCK ? (
            <div className={styles['preview-cart-item__bottom']}>
              <ActionButtons product={product} onRemove={handleDeleteRequested} size='small' buttonsTemplate={[  'comment', 'favorite' ]} />
              <ControlCart size="small" isTablet={true} product={product} noTotals />
              <ActionButtons product={product} onRemove={handleDeleteRequested} size='small' buttonsTemplate={[  'delete' ]} />
            </div>) : (
            <DeleteProduct
              className={styles['preview-cart-item__delete']}
              onClick={handleDeleteRequested}
            />
          )
        )
      }
    </div>

    {/* {!OUT_STOCK && (
      <div className={styles['preview-cart-item__bottom']}>
        <CommentButton product={product} />
        <ActionButtons
          product={product}
          onRemove={onRemoveRequested}
          buttonsTemplate={[  'favorite' ]}
        />
        <ControlCart size="small" isTablet={true} product={product} noTotals />
      </div>
    )} */}

    {OUT_STOCK && (
      <div className={styles['preview-cart-item__bottom']}>
        <DeleteProduct
          className={styles['preview-cart-item__delete']}
          onClick={(e) => onRemoveRequested(e, product.id)}
        />
      </div>
    )}
  </div>
}

export default CartPosition
